import {
  AccessRole,
  AccessRoleCatalog,
  BASE_ACCESS_ROLE,
  CapabilityDescriptor,
  DomainDescriptor,
  DomainRoleCapabilities,
  DomainRoles,
} from '@trustero/trustero-api-web/lib/authz/authz_pb'
import {
  CAPABILITY,
  PERMISSION_RULE,
  Permission,
  Permissions,
} from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import { UserRolesAbsoluteRoutes } from 'src/components/Reusable/RootPage/RootPage.constants'

export const USER_ROLES_TAB_KEY = 'user-role'

// Relative to the users/* route, so both SaasProviderApp and SaasBuyerApp get it.
export const USER_ROLE_SHOW_SUBROUTE = 'roles/show/:userRoleId'

export const userRolePath = (pageContext: string, roleId: string): string =>
  `/${pageContext}/${UserRolesAbsoluteRoutes.SHOW}/${roleId}`

export const userRolesIndexPath = (pageContext: string): string =>
  `/${pageContext}/${UserRolesAbsoluteRoutes.INDEX}`

export const getBaseRoleName = (baseRole: BASE_ACCESS_ROLE): string => {
  switch (baseRole) {
    case BASE_ACCESS_ROLE.ADMIN:
      return 'Admin'
    case BASE_ACCESS_ROLE.USER:
      return 'User'
    case BASE_ACCESS_ROLE.AUDITOR:
      return 'Auditor'
    case BASE_ACCESS_ROLE.AUDITOR_QC:
      return 'Auditor QC'
    case BASE_ACCESS_ROLE.READ_ONLY:
      return 'Read Only'
    case BASE_ACCESS_ROLE.TP_PUBLISHER:
      return 'Trust Portal Publisher'
    case BASE_ACCESS_ROLE.TP_APPROVER:
      return 'Trust Portal Approver'
    case BASE_ACCESS_ROLE.NO_ACCESS:
      return 'No Access'
    default:
      return 'Unknown'
  }
}

export type PermissionRuleColumn = {
  rule: PERMISSION_RULE
  label: string
  headerLabel?: string
}

/**
 * The columns of the capability grid. These are the only permission-rule strings the
 * frontend owns - domain, capability and domain-role names all come from the catalog,
 * but these are column headers over a small fixed enum.
 */
export const PERMISSION_RULE_COLUMNS: PermissionRuleColumn[] = [
  { rule: PERMISSION_RULE.ALWAYS, label: 'Allow' },
  { rule: PERMISSION_RULE.IF_OWNER, label: 'If Owner' },
  {
    rule: PERMISSION_RULE.IF_OWNER_OR_ASSIGNEE,
    label: 'If Owner or Assignee',
    headerLabel: 'If Owner/Assignee',
  },
  {
    rule: PERMISSION_RULE.CONTROLLED_BY_LABEL,
    label: 'Controlled by label',
    headerLabel: 'Controlled by Label',
  },
  { rule: PERMISSION_RULE.NEVER, label: 'Deny' },
]

/**
 * Rules that depend on label-based RBAC scoping. One rule now: whether a label admits or
 * refuses is carried by the user group's association, not by the rule.
 */
export const LABEL_PERMISSION_RULES = new Set([
  PERMISSION_RULE.CONTROLLED_BY_LABEL,
])

/**
 * Rules that scope access to the record's owner or assignee.
 */
export const OWNERSHIP_PERMISSION_RULES = new Set([
  PERMISSION_RULE.IF_OWNER,
  PERMISSION_RULE.IF_OWNER_OR_ASSIGNEE,
])

/**
 * Domains whose records carry an owner (Requests) or an owner and an assignee
 * (Controls, Policies, Risks, Vendors). Ownership-scoped rules are meaningless
 * anywhere else, so those columns are dropped for every other domain.
 */
export const OWNERSHIP_DOMAIN_KEYS = new Set([
  'controls',
  'policies',
  'risks',
  'vendors',
  'requests',
])

/**
 * Domains where label-conditional rules are enforced end-to-end. The RPC gate treats any
 * non-NEVER conditional rule as sufficient, so a domain whose handlers don't narrow with
 * CanAccessResource would admit the call unscoped - offering the column there would hand
 * out access the label was meant to restrict. Add a domain once it enforces the check.
 */
const LABEL_PERMISSION_DOMAIN_KEYS = new Set(['controls', 'policies'])

/**
 * The rule columns to render for one domain. Label columns need both the feature flag
 * and a domain that enforces them; ownership columns only appear on domains whose
 * records have an owner.
 */
export const getPermissionRuleColumns = (
  domainKey: string,
  isIfGrantedPermissionEnabled: boolean,
): typeof PERMISSION_RULE_COLUMNS => {
  const showOwnershipRules = OWNERSHIP_DOMAIN_KEYS.has(domainKey)
  const showLabelRules =
    isIfGrantedPermissionEnabled && LABEL_PERMISSION_DOMAIN_KEYS.has(domainKey)
  return PERMISSION_RULE_COLUMNS.filter((col) => {
    if (LABEL_PERMISSION_RULES.has(col.rule)) return showLabelRules
    if (OWNERSHIP_PERMISSION_RULES.has(col.rule)) return showOwnershipRules
    return true
  })
}

/**
 * Display-only renames of catalog domain names. The catalog - and every role, capability
 * and override behind it - keeps the original name; only the label on screen changes.
 * Chats is deliberately not renamed to "Trustero Intelligence": the catalog has its own
 * Trustero Intelligence domain gating that page, and two rows under one name would be
 * indistinguishable while controlling different permissions.
 */
const DOMAIN_DISPLAY_NAMES: Record<string, string> = {}

export const getDomainDisplayName = (domain: {
  key: string
  name: string
}): string => DOMAIN_DISPLAY_NAMES[domain.key] ?? domain.name

/**
 * Feature rows in the order the show page lists them: alphabetical by the name shown
 * in the Feature column.
 */
export const sortDomainsByDisplayName = <
  T extends { key: string; name: string },
>(
  domains: T[],
): T[] =>
  [...domains].sort((a, b) =>
    getDomainDisplayName(a).localeCompare(getDomainDisplayName(b)),
  )

/**
 * Options for the Feature Access selector, which governs access to the feature
 * and controls page and navigation access via the domain's HAS_PAGE_ACCESS capability.
 */
export const FEATURE_ACCESS_OPTIONS = ['Always', 'Never']

/**
 * Options for the Record Access selector, which governs access to individual records.
 * These overrides change only record visibility; page access is independent.
 */
export const RECORD_ACCESS_OPTIONS = [
  'Allow',
  'If Owner',
  'If Owner or Assignee',
  'Controlled by Label',
]

export const RECORD_ACCESS_DOMAIN_KEYS = new Set(['controls', 'policies'])

/**
 * Mapping from domain key to the record visibility capability for that domain.
 */
export const RECORD_ACCESS_CAPABILITY_BY_DOMAIN: Record<string, CAPABILITY> = {
  controls: CAPABILITY.CAN_VIEW_CONTROLS,
  policies: CAPABILITY.CAN_VIEW_POLICIES,
}

/**
 * Returns whether a capability is a Record Access capability for a given domain
 * (or across all record-access domains if domainKey is not specified).
 */
export const isRecordAccessCapability = (
  capability: CAPABILITY | number,
  domainKey?: string,
): boolean => {
  if (domainKey) {
    return RECORD_ACCESS_CAPABILITY_BY_DOMAIN[domainKey] === capability
  }
  return Object.values(RECORD_ACCESS_CAPABILITY_BY_DOMAIN).includes(
    capability as CAPABILITY,
  )
}

/**
 * Returns the label for the record visibility/access row in the capability matrix.
 */
export const getRecordVisibilityLabel = (
  domainKey: string,
  domainDisplayName: string,
): string => {
  if (domainKey === 'controls') return 'Control Record Visibility'
  if (domainKey === 'policies') return 'Policy Record Visibility'
  return `${domainDisplayName} Record Visibility`
}

/**
 * Rules allowed for Page Access capabilities. Page access can only be unconditionally
 * granted (Always) or denied (Never), not conditioned on ownership or labels.
 */
export const PAGE_ACCESS_ALLOWED_RULES = new Set<PERMISSION_RULE>([
  PERMISSION_RULE.ALWAYS,
  PERMISSION_RULE.NEVER,
])

/**
 * Set of capability IDs that represent page access across all domains.
 */
export const PAGE_ACCESS_CAPABILITY_SET = new Set<number>(
  Object.entries(CAPABILITY)
    .filter(([key]) => key.startsWith('HAS_PAGE_ACCESS_'))
    .map(([, val]) => val as number),
)

/**
 * Returns whether a capability is a "Has Page Access [Domain]" capability.
 * Checks both the capability enum ID and optional capability name (e.g. from catalog descriptor).
 */
export const isPageAccessCapability = (
  capability: CAPABILITY | number,
  capabilityName?: string,
): boolean => {
  if (PAGE_ACCESS_CAPABILITY_SET.has(capability)) {
    return true
  }
  if (
    capabilityName &&
    capabilityName.trim().toLowerCase().startsWith('has page access')
  ) {
    return true
  }
  return false
}

/**
 * Returns whether a rule is valid/allowed for a specific capability.
 * Page access capabilities only allow Always and Never.
 */
export const isRuleAllowedForCapability = (
  capability: CAPABILITY | number,
  rule: PERMISSION_RULE,
  capabilityName?: string,
): boolean => {
  if (isPageAccessCapability(capability, capabilityName)) {
    return PAGE_ACCESS_ALLOWED_RULES.has(rule)
  }
  return true
}

/**
 * Sorts capabilities so that "Has Page Access" capabilities appear first,
 * maintaining the relative order of all other capabilities.
 */
export const sortCapabilitiesWithPageAccessOnTop = <
  T extends { capability: CAPABILITY | number; name?: string },
>(
  capabilities: T[],
): T[] => {
  return [...capabilities].sort((a, b) => {
    const aIsPageAccess = isPageAccessCapability(a.capability, a.name)
    const bIsPageAccess = isPageAccessCapability(b.capability, b.name)
    if (aIsPageAccess && !bIsPageAccess) return -1
    if (!aIsPageAccess && bIsPageAccess) return 1
    return 0
  })
}

export type CatalogIndex = {
  domains: DomainDescriptor.AsObject[]
  capabilitiesByDomain: Map<number, CapabilityDescriptor.AsObject[]>
  rolesByDomain: Map<number, DomainRoleCapabilities.AsObject[]>
  rulesByDomainRole: Map<string, Map<CAPABILITY, PERMISSION_RULE>>
  defaultsByBaseRole: Map<BASE_ACCESS_ROLE, DomainRoles.AsObject>
}

// DomainRoles field names arrive snake_cased from the proto; toObject() keys them camelCase.
const toCamelCase = (key: string): string =>
  key.replace(/_([a-z])/g, (_, letter: string) => letter.toUpperCase())

const domainRoleKey = (domain: number, domainRole: number): string =>
  `${domain}:${domainRole}`

/**
 * Reshape the catalog into the lookups the show page needs, so rendering 28 accordions
 * doesn't rescan four flat lists each time.
 */
export const indexCatalog = (catalog: AccessRoleCatalog): CatalogIndex => {
  const capabilitiesByDomain = new Map<
    number,
    CapabilityDescriptor.AsObject[]
  >()
  catalog.getCapabilitiesList().forEach((capability) => {
    const item = capability.toObject()
    const existing = capabilitiesByDomain.get(item.domain)
    if (existing) {
      existing.push(item)
    } else {
      capabilitiesByDomain.set(item.domain, [item])
    }
  })
  capabilitiesByDomain.forEach((caps, domain) => {
    capabilitiesByDomain.set(domain, sortCapabilitiesWithPageAccessOnTop(caps))
  })

  const rolesByDomain = new Map<number, DomainRoleCapabilities.AsObject[]>()
  const rulesByDomainRole = new Map<string, Map<CAPABILITY, PERMISSION_RULE>>()
  catalog.getDomainRolesList().forEach((domainRole) => {
    const item = domainRole.toObject()
    const existing = rolesByDomain.get(item.domain)
    if (existing) {
      existing.push(item)
    } else {
      rolesByDomain.set(item.domain, [item])
    }
    rulesByDomainRole.set(
      domainRoleKey(item.domain, item.domainRole),
      new Map(item.permissionsList.map((p) => [p.capability, p.rule])),
    )
  })

  const defaultsByBaseRole = new Map<BASE_ACCESS_ROLE, DomainRoles.AsObject>()
  catalog.getBaseDefaultsList().forEach((baseDefault) => {
    const domainRoles = baseDefault.getDomainRoles()
    if (domainRoles) {
      defaultsByBaseRole.set(
        baseDefault.getBaseAccessRole(),
        domainRoles.toObject(),
      )
    }
  })

  return {
    domains: catalog.getDomainsList().map((domain) => domain.toObject()),
    capabilitiesByDomain,
    rolesByDomain,
    rulesByDomainRole,
    defaultsByBaseRole,
  }
}

// Reading a DomainRoles enum by the catalog's domain key. Every enum's UNSPECIFIED is 0,
// which is how the backend distinguishes "not overridden" from a real choice.
const domainRoleValue = (
  domainRoles: DomainRoles.AsObject | undefined,
  domainKey: string,
): number => {
  if (!domainRoles) return 0
  const value = (domainRoles as unknown as Record<string, number | undefined>)[
    toCamelCase(domainKey)
  ]
  return value || 0
}

/**
 * The role a user role actually has in one domain: its override if it set one, else the
 * base role's default. Mirrors rbac.MergeDomainRoles.
 */
export const resolveDomainRole = (
  index: CatalogIndex,
  role: AccessRole,
  domain: DomainDescriptor.AsObject,
): number => {
  const roleObj = role as unknown as AccessRole.AsObject | undefined
  const overridesObj =
    typeof role?.getDomainOverrides === 'function'
      ? role.getDomainOverrides()?.toObject()
      : roleObj?.domainOverrides
  const override = domainRoleValue(overridesObj, domain.key)
  if (override) return override
  const baseRole =
    typeof role?.getBaseAccessRole === 'function'
      ? role.getBaseAccessRole()
      : roleObj?.baseAccessRole
  return baseRole !== undefined
    ? domainRoleValue(index.defaultsByBaseRole.get(baseRole), domain.key)
    : 0
}

export const getPermissionOverrides = (
  role: AccessRole,
): Map<CAPABILITY, PERMISSION_RULE> => {
  if (!role) return new Map()
  const roleObj = role as unknown as AccessRole.AsObject | undefined
  const permList =
    typeof role.getPermissionOverrides === 'function'
      ? role.getPermissionOverrides()?.getPermissionsList() || []
      : roleObj?.permissionOverrides?.permissionsList || []

  const map = new Map<CAPABILITY, PERMISSION_RULE>()
  permList.forEach((p: Permission | Permission.AsObject) => {
    const capability =
      typeof (p as Permission).getCapability === 'function'
        ? (p as Permission).getCapability()
        : (p as Permission.AsObject).capability
    const rule =
      typeof (p as Permission).getRule === 'function'
        ? (p as Permission).getRule()
        : (p as Permission.AsObject).rule
    if (capability !== undefined && rule !== undefined) {
      map.set(capability, rule)
    }
  })
  return map
}

/**
 * The rule in force for one capability. Overrides win, then the domain role's map, then
 * NEVER - a capability absent from the map is denied, same as rbac.HasCapabilities.
 * Takes the domain role as an argument rather than resolving it so the accordion can
 * re-derive the whole grid when the dropdown changes, with no refetch.
 */
export const resolveCapabilityRule = (
  index: CatalogIndex,
  overrides: Map<CAPABILITY, PERMISSION_RULE>,
  domain: number,
  domainRole: number,
  capability: CAPABILITY,
): PERMISSION_RULE => {
  const override = overrides.get(capability)
  if (override !== undefined) return override
  const rule = index.rulesByDomainRole
    .get(domainRoleKey(domain, domainRole))
    ?.get(capability)
  return rule ?? PERMISSION_RULE.NEVER
}

/**
 * Capability numbering encodes its domain: domain = capability / 1000, the same
 * convention rbac's domainRegistry is built on.
 */
export const capabilityDomain = (capability: CAPABILITY | number): number =>
  Math.floor(capability / 1000)

/**
 * Whether a role resolves a capability to an unconditional grant. Anything conditional
 * counts as not allowed, so this only answers "may this role definitely do it" - which
 * is what picking a page the role can open needs.
 */
export const isCapabilityAlwaysAllowed = (
  index: CatalogIndex,
  role: AccessRole,
  capability: CAPABILITY,
): boolean => {
  const domain = index.domains.find(
    (d) => d.domain === capabilityDomain(capability),
  )
  if (!domain) return false
  return (
    resolveCapabilityRule(
      index,
      getPermissionOverrides(role),
      domain.domain,
      resolveDomainRole(index, role, domain),
      capability,
    ) === PERMISSION_RULE.ALWAYS
  )
}

/**
 * The default domain role enum value for a domain from the base access role.
 */
export const getBaseDomainRoleDefault = (
  index: CatalogIndex,
  baseRole: BASE_ACCESS_ROLE,
  domainKey: string,
): number => {
  return domainRoleValue(index.defaultsByBaseRole.get(baseRole), domainKey)
}

/**
 * Returns whether a domain's role is overridden compared to the base role's default.
 */
export const isDomainRoleOverridden = (
  index: CatalogIndex,
  role: AccessRole,
  domain: DomainDescriptor.AsObject,
): boolean => {
  const roleObj = role as unknown as AccessRole.AsObject | undefined
  const overridesObj =
    typeof role?.getDomainOverrides === 'function'
      ? role.getDomainOverrides()?.toObject()
      : roleObj?.domainOverrides
  const override = domainRoleValue(overridesObj, domain.key)
  const baseRole =
    typeof role?.getBaseAccessRole === 'function'
      ? role.getBaseAccessRole()
      : roleObj?.baseAccessRole
  const baseDefault =
    baseRole !== undefined
      ? getBaseDomainRoleDefault(index, baseRole, domain.key)
      : 0
  return override !== 0 && override !== baseDefault
}

/**
 * The default rule for a capability defined strictly by the given domain role (without overrides).
 */
export const getCapabilityDomainRoleDefaultRule = (
  index: CatalogIndex,
  domain: number,
  domainRole: number,
  capability: CAPABILITY,
): PERMISSION_RULE => {
  const rule = index.rulesByDomainRole
    .get(domainRoleKey(domain, domainRole))
    ?.get(capability)
  return rule ?? PERMISSION_RULE.NEVER
}

/**
 * Get human-readable label for a PERMISSION_RULE, including the rules the matrix has
 * no column for.
 */
export const getPermissionRuleLabel = (rule: PERMISSION_RULE): string => {
  const col = PERMISSION_RULE_COLUMNS.find((c) => c.rule === rule)
  if (col) return col.label
  if (rule === PERMISSION_RULE.IF_TRUSTERO_USER) return 'If Trustero User'
  return 'Deny'
}

/**
 * Checks if a specific capability has an active override in permission overrides.
 */
export const isCapabilityOverridden = (
  overrides: Map<CAPABILITY, PERMISSION_RULE>,
  capability: CAPABILITY,
  defaultRule: PERMISSION_RULE,
): boolean => {
  const override = overrides.get(capability)
  return override !== undefined && override !== defaultRule
}

/**
 * Counts total overrides for a domain (domain role override if any + capability overrides).
 */
export const countDomainOverrides = (
  index: CatalogIndex,
  role: AccessRole,
  domain: DomainDescriptor.AsObject,
): {
  domainRoleOverridden: boolean
  capabilityOverrideCount: number
  totalOverrideCount: number
} => {
  const domainRoleOverridden = isDomainRoleOverridden(index, role, domain)
  const resolvedRole = resolveDomainRole(index, role, domain)
  const overrides = getPermissionOverrides(role)
  const domainCaps = index.capabilitiesByDomain.get(domain.domain) || []

  let capabilityOverrideCount = 0
  domainCaps.forEach((cap) => {
    const defaultRule = getCapabilityDomainRoleDefaultRule(
      index,
      domain.domain,
      resolvedRole,
      cap.capability,
    )
    if (isCapabilityOverridden(overrides, cap.capability, defaultRule)) {
      capabilityOverrideCount += 1
    }
  })

  const totalOverrideCount =
    (domainRoleOverridden ? 1 : 0) + capabilityOverrideCount

  return {
    domainRoleOverridden,
    capabilityOverrideCount,
    totalOverrideCount,
  }
}

/**
 * Dynamically set the domain role field on DomainRoles proto message by domain key.
 */
export const setDomainRoleField = (
  domainRoles: DomainRoles,
  domainKey: string,
  value: number,
): void => {
  const camel = toCamelCase(domainKey)
  const setterName = `set${camel.charAt(0).toUpperCase()}${camel.slice(
    1,
  )}` as keyof DomainRoles
  if (typeof domainRoles[setterName] === 'function') {
    const setter = domainRoles[setterName] as (val: number) => void
    setter.call(domainRoles, value)
  }
}

/**
 * Builds the updated DomainRoles proto message with the specified domain updated.
 * Sets 0 (UNSPECIFIED) if the role matches the base access role default.
 */
export const buildUpdatedDomainOverrides = (
  role: AccessRole,
  domainKey: string,
  nextDomainRole: number,
  baseDefaultDomainRole: number,
): DomainRoles => {
  const domainOverrides = new DomainRoles()

  const roleObj = role as unknown as AccessRole.AsObject | undefined
  const existingObj: Record<string, number | undefined> | undefined =
    typeof role?.getDomainOverrides === 'function'
      ? role.getDomainOverrides()?.toObject()
      : roleObj?.domainOverrides

  if (existingObj) {
    Object.entries(existingObj).forEach(([key, val]) => {
      if (typeof val === 'number' && val !== 0) {
        setDomainRoleField(domainOverrides, key, val)
      }
    })
  }

  const overrideValue =
    nextDomainRole === baseDefaultDomainRole ? 0 : nextDomainRole
  setDomainRoleField(domainOverrides, domainKey, overrideValue)
  return domainOverrides
}

/**
 * Builds the updated Permissions proto message with capability overrides for the specified domain.
 * Preserves overrides from other domains while clearing capability overrides that match domain role defaults.
 */
export const buildUpdatedPermissionOverrides = (
  role: AccessRole,
  index: CatalogIndex,
  domainId: number,
  selectedDomainRole: number,
  ruleEdits: Map<CAPABILITY, PERMISSION_RULE>,
): Permissions => {
  const domainCaps = index.capabilitiesByDomain.get(domainId) || []
  const domainCapSet = new Set(domainCaps.map((c) => c.capability))

  const savedOverrides = getPermissionOverrides(role)
  const updatedPermissions: Permission[] = []

  // Preserve existing overrides that belong to other domains
  savedOverrides.forEach((rule, capability) => {
    if (!domainCapSet.has(capability)) {
      updatedPermissions.push(
        new Permission().setCapability(capability).setRule(rule),
      )
    }
  })

  // Apply effective rules for the current domain
  domainCaps.forEach((cap) => {
    const effectiveRule =
      ruleEdits.get(cap.capability) ??
      resolveCapabilityRule(
        index,
        savedOverrides,
        domainId,
        selectedDomainRole,
        cap.capability,
      )

    const defaultRule = getCapabilityDomainRoleDefaultRule(
      index,
      domainId,
      selectedDomainRole,
      cap.capability,
    )

    if (effectiveRule !== defaultRule) {
      if (
        !isRuleAllowedForCapability(cap.capability, effectiveRule, cap.name)
      ) {
        return
      }
      const perm = new Permission()
        .setCapability(cap.capability)
        .setRule(effectiveRule)
      updatedPermissions.push(perm)
    }
  })

  return new Permissions().setPermissionsList(updatedPermissions)
}
